import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, AlertCircle, Trash2 } from 'lucide-react';
import {
  Alert,
  AlertDescription,
  Pagination,
  Button,
  Skeleton,
  useAuth,
  UI_CONFIG,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@aaas/shared';
import { useWidgetListQuery, useDeleteWidgetMutation } from '../hooks';
import { WidgetCard } from '../components/WidgetCard';

const WidgetListPage = () => {
  const navigate = useNavigate();
  const { user, currentWorkspace } = useAuth();
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(UI_CONFIG.PAGINATION.DEFAULT_PAGE_SIZE);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [widgetToDelete, setWidgetToDelete] = useState<string | null>(null);

  // Calculate skip for backend (0-based offset)
  const skip = (currentPage - 1) * pageSize;

  // React Query for widget list
  const {
    data: widgetResponse,
    isLoading: loading,
    error,
  } = useWidgetListQuery({
    skip,
    limit: pageSize,
    enabled: !!user && !!currentWorkspace,
  });

  const deleteMutation = useDeleteWidgetMutation();

  // Extract data from response
  const widgets = widgetResponse?.items || [];
  const totalItems = widgetResponse?.total || 0;
  const totalPages = Math.ceil(totalItems / pageSize) || 0;

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setCurrentPage(1); // Reset to first page when changing page size
  };

  const handleEdit = (id: string) => {
    navigate(`/kms/widgets/${id}/edit`);
  };

  const handleDelete = (id: string) => {
    setWidgetToDelete(id);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!widgetToDelete) return;

    try {
      await deleteMutation.mutateAsync(widgetToDelete);
      setDeleteDialogOpen(false);
      setWidgetToDelete(null);
    } catch (error) {
      console.error('Failed to delete widget:', error);
    }
  };

  // WidgetList Skeleton Component
  const WidgetListSkeleton = () => (
    <div className="space-y-6">
      {/* Header Skeleton */}
      <div className="flex justify-between items-center">
        <Skeleton variant="text" className="w-32 h-8" />
        <Skeleton variant="rectangular" className="w-32 h-10" />
      </div>

      {/* Cards Grid Skeleton */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {Array.from({ length: Math.min(pageSize, 8) }).map((_, index) => (
          <div key={index} className="bg-white rounded-lg border shadow-sm p-6">
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Skeleton variant="text" className="w-32 h-5" />
                <Skeleton variant="rectangular" className="w-16 h-6" />
              </div>
              <Skeleton variant="text" className="w-full h-4" />
              <Skeleton variant="text" className="w-3/4 h-4" />
              <div className="flex justify-between">
                <Skeleton variant="text" className="w-20 h-3" />
                <Skeleton variant="text" className="w-20 h-3" />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Pagination Skeleton */}
      <div className="flex justify-between items-center">
        <Skeleton variant="text" className="w-32 h-4" />
        <div className="flex gap-2">
          <Skeleton variant="rectangular" className="w-8 h-8" />
          <Skeleton variant="rectangular" className="w-8 h-8" />
          <Skeleton variant="rectangular" className="w-8 h-8" />
        </div>
        <Skeleton variant="rectangular" className="w-24 h-8" />
      </div>
    </div>
  );

  if (loading) {
    return <WidgetListSkeleton />;
  }

  const widgetToDeleteData = widgetToDelete ? widgets.find((w) => w.id === widgetToDelete) : null;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold">Widgets</h1>

        {widgets.length > 0 && (
          <Button
            onClick={() => navigate('/kms/widgets/create')}
            className="bg-green-600 hover:bg-green-700"
          >
            <Plus className="w-4 h-4 mr-2" />
            Create Widget
          </Button>
        )}
      </div>

      {/* Error Alert */}
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            {error instanceof Error ? error.message : 'Failed to load widgets'}
          </AlertDescription>
        </Alert>
      )}

      {/* Delete Error Alert */}
      {deleteMutation.isError && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>Failed to delete widget. Please try again.</AlertDescription>
        </Alert>
      )}

      {/* Widget Cards Grid */}
      <div className="space-y-6">
        {widgets.length === 0 && !loading ? (
          <div className="text-center py-12">
            <div className="text-gray-500 text-lg mb-4">No widgets found</div>
            <Button
              onClick={() => navigate('/kms/widgets/create')}
              className="bg-green-600 hover:bg-green-700"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create your first widget
            </Button>
          </div>
        ) : (
          <>
            {/* Cards Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {widgets.map((widget) => (
                <WidgetCard
                  key={widget.id}
                  widget={widget}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                />
              ))}
            </div>

            {/* Pagination - Only show if more than 1 page */}
            {totalPages > 1 && (
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                pageSize={pageSize}
                totalItems={totalItems}
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
                pageSizeOptions={[...UI_CONFIG.PAGINATION.PAGE_SIZE_OPTIONS]}
                showTotal={true}
                showPageSizeSelector={true}
              />
            )}
          </>
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Widget</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete widget "{widgetToDeleteData?.name}"? This action
              cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteDialogOpen(false)}
              disabled={deleteMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={confirmDelete}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? (
                'Deleting...'
              ) : (
                <>
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WidgetListPage;
